import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Users, Repeat, Gift, Award } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import Seo, { FaqJsonLd, BreadcrumbJsonLd } from "@/components/common/Seo";
import { VEHICLES, ONE_TIME, DETAIL_TYPES, PLANS, CERAMIC, WAYS_TO_SAVE, FAQ, planPrice } from "@/data/pricing";
import { HERO_IMG } from "@/data/site";

const SAVE_ICONS = [Sparkles, Users, Repeat, Award, Gift];

export default function Pricing() {
  const [vehicle, setVehicle] = useState("sedan");
  const [detailType, setDetailType] = useState("full");
  const baseMap = ONE_TIME[detailType];

  return (
    <div data-testid="pricing-page">
      <Seo
        title="Car Detailing Prices in Ottawa | Reverence Autos"
        description="Transparent CAD pricing for mobile car detailing in Ottawa — interior, full detail, express and ceramic coating. Subscriptions save up to 30%. We come to you across Orleans, Kanata, Barrhaven & more."
        path="/pricing"
      />
      <FaqJsonLd faq={FAQ} />
      <BreadcrumbJsonLd items={[{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]} />

      {/* Header */}
      <section className="relative overflow-hidden bg-brand-charcoal">
        <img src={HERO_IMG} alt="The Reverence Autos team" className="absolute inset-0 h-full w-full object-cover object-center opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/75 via-brand-charcoal/60 to-brand-charcoal/90" />
        <div className="container-rv relative py-20 text-center lg:py-28">
          <Reveal>
            <p className="overline text-brand-red">Pricing · All prices CAD</p>
            <h1 className="mx-auto mt-4 max-w-3xl font-serif text-5xl font-bold leading-tight text-white sm:text-6xl">
              Showroom results, honest prices
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-brand-silver">
              Mobile detailing across Ottawa — we come to you at no extra charge. Summer Special: 10% off, auto-applied at checkout. All prices shown before tax.
            </p>
          </Reveal>
        </div>
      </section>

      {/* One-time details table */}
      <section className="bg-white">
        <div className="container-rv py-20 lg:py-24">
          <Reveal className="mb-10">
            <h2 className="font-serif text-3xl font-bold text-brand-charcoal sm:text-4xl">One-time details</h2>
            <p className="mt-3 text-brand-muted">Interior & Full details are priced by vehicle size; Express is a flat rate. No subscription, no commitment. All prices before tax.</p>
          </Reveal>

          <div className="overflow-hidden rounded-2xl border border-brand-silver/40">
            {/* head */}
            <div className="grid grid-cols-4 bg-brand-charcoal text-white">
              <div className="p-4 text-sm font-semibold sm:p-6">Service</div>
              {VEHICLES.map((v) => (
                <div key={v.id} className="p-4 text-center text-sm font-semibold sm:p-6">{v.label}</div>
              ))}
            </div>
            {/* Interior */}
            <Row name="Interior Detail" sub="Deep interior clean" prices={ONE_TIME.interior} />
            {/* Full - best value */}
            <Row name="Full Detail" sub="Interior + Exterior" prices={ONE_TIME.full} badge="Best Value" highlight />
            {/* Express */}
            <div className="grid grid-cols-4 items-center border-t border-brand-silver/30 bg-brand-offwhite">
              <div className="col-span-1 p-4 sm:p-6">
                <p className="font-semibold text-brand-charcoal">Express 90-Min Refresh</p>
                <p className="mt-1 text-xs text-brand-muted">In and out in 90 minutes. Perfect between full details.</p>
              </div>
              <div className="col-span-3 p-4 text-center sm:p-6">
                <span className="font-serif text-2xl font-bold text-brand-charcoal">$75</span>
                <span className="ml-2 text-sm text-brand-muted">flat · any vehicle size · + tax</span>
              </div>
            </div>
          </div>
          <Link to="/book" className="btn-red mt-8" data-testid="pricing-onetime-book">Book a one-time detail <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* Ways to save */}
      <section className="bg-brand-charcoal text-white">
        <div className="container-rv py-16 lg:py-20">
          <Reveal className="mb-10">
            <p className="overline text-brand-red">Ways to Save</p>
            <h2 className="mt-3 font-serif text-3xl font-bold sm:text-4xl">Stack the savings</h2>
            <p className="mt-3 max-w-xl text-brand-silver text-sm">
              Multi-vehicle stacks with subscription discounts (up to 35% off per vehicle). Nothing stacks with the summer promo or loyalty reward. One-time details are capped at 25% off any single vehicle.
            </p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {WAYS_TO_SAVE.map((w, i) => {
              const Icon = SAVE_ICONS[i];
              return (
                <Reveal key={w.title} delay={i * 0.06}>
                  <div className={`h-full rounded-2xl border p-5 ${w.accent ? "border-brand-red/60 bg-brand-red/10" : "border-white/10 bg-white/[0.03]"}`}>
                    <Icon className="h-6 w-6 text-brand-red" />
                    <p className="mt-4 font-semibold">{w.title}</p>
                    <p className="mt-2 text-sm text-brand-silver">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Subscriptions */}
      <section className="bg-white">
        <div className="container-rv py-20 lg:py-28">
          <Reveal className="mb-4 text-center">
            <p className="overline text-brand-red">Subscription plans</p>
            <h2 className="mx-auto mt-3 max-w-2xl font-serif text-3xl font-bold text-brand-charcoal sm:text-4xl">
              Always drive a clean car — for less per month than one dinner out.
            </h2>
            <p className="mt-3 text-sm text-brand-muted">First visit at full one-time price; recurring visits get the discount. Cancel anytime — no contracts. Prices before tax.</p>
          </Reveal>

          {/* toggles */}
          <div className="mb-10 flex flex-col items-center gap-3">
            <div className="inline-flex rounded-full border border-brand-silver/50 p-1">
              {DETAIL_TYPES.map((d) => (
                <button key={d.id} data-testid={`sub-type-${d.id}`} onClick={() => setDetailType(d.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${detailType === d.id ? "bg-brand-charcoal text-white" : "text-brand-muted hover:text-brand-charcoal"}`}>
                  {d.label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-full border border-brand-silver/50 p-1">
              {VEHICLES.map((v) => (
                <button key={v.id} data-testid={`sub-vehicle-${v.id}`} onClick={() => setVehicle(v.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${vehicle === v.id ? "bg-brand-red text-white" : "text-brand-muted hover:text-brand-charcoal"}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5">
            {PLANS.map((p, i) => {
              const per = planPrice(baseMap[vehicle], p.off);
              return (
                <Reveal key={p.id} delay={i * 0.06}>
                  <div data-testid={`sub-plan-${p.id}`} className={`card-rv relative flex h-full flex-col p-6 ${p.badge ? "border-brand-red/60 shadow-[0_24px_60px_-40px_rgba(200,16,46,0.5)]" : ""}`}>
                    {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-red px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-white">{p.badge}</span>}
                    <p className="font-serif text-xl font-bold text-brand-charcoal">{p.name}</p>
                    <p className="mt-1 text-xs text-brand-muted">{p.freq}</p>
                    <div className="mt-5 flex items-baseline gap-1">
                      <span className="font-serif text-4xl font-bold text-brand-charcoal">${per}</span>
                      <span className="text-xs text-brand-muted">/visit</span>
                    </div>
                    <p className="mt-1 text-xs font-semibold text-brand-red">{Math.round(p.off * 100)}% off recurring</p>
                    {p.annual && <p className="mt-2 text-xs text-brand-muted">${per * 12} / year prepaid</p>}
                    <Link to="/book" className="btn-outline-dark mt-auto pt-4 w-full !mt-6">Choose {p.name}</Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ceramic */}
      <section className="bg-brand-offwhite">
        <div className="container-rv py-20 lg:py-28">
          <Reveal className="mb-12 text-center">
            <p className="overline text-brand-red">Ceramic coating</p>
            <h2 className="mx-auto mt-3 max-w-2xl font-serif text-3xl font-bold text-brand-charcoal sm:text-4xl">Multi-year armor for Ottawa roads</h2>
            <p className="mx-auto mt-3 max-w-xl text-brand-muted">Protects against Ottawa winters, road salt and UV. Every package includes full wash, paint decontamination and light polish prep. Add $100 for vans/trucks.</p>
          </Reveal>
          <div className="grid gap-7 lg:grid-cols-3">
            {CERAMIC.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <div data-testid={`ceramic-${c.id}`}
                  className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-brand-silver/50 p-8"
                  style={{ background: "linear-gradient(160deg,#ffffff 0%,#f2f3f5 60%,#e2e4e7 100%)" }}>
                  {c.popular && <span className="absolute right-6 top-6 rounded-full bg-brand-red px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-wider text-white">Popular</span>}
                  <p className="overline text-brand-muted">{c.protection}</p>
                  <h3 className="mt-3 font-serif text-2xl font-bold text-brand-charcoal">{c.name}</h3>
                  <div className="mt-4 font-serif text-5xl font-bold chrome-text">${c.price}</div>
                  <p className="mt-4 flex-1 text-sm text-brand-muted">{c.desc}</p>
                  <Link to="/book" className="btn-red mt-6 w-full">Protect my car</Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="container-rv py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="overline text-brand-red">Questions & answers</p>
            <h2 className="mt-3 font-serif text-3xl font-bold text-brand-charcoal sm:text-4xl">Ottawa detailing FAQ</h2>
          </Reveal>
          <div className="grid gap-x-12 gap-y-10 lg:grid-cols-2">
            {FAQ.map((f) => (
              <Reveal key={f.q}>
                <h3 className="font-serif text-xl font-bold text-brand-charcoal">{f.q}</h3>
                <p className="mt-3 text-brand-muted leading-relaxed">{f.a}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ name, sub, prices, badge, highlight }) {
  return (
    <div className={`grid grid-cols-4 items-center border-t border-brand-silver/30 ${highlight ? "bg-brand-red/[0.04]" : "bg-white"}`}>
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-brand-charcoal">{name}</p>
          {badge && <span className="rounded-full bg-brand-red px-2 py-0.5 text-[0.55rem] font-semibold uppercase tracking-wider text-white">{badge}</span>}
        </div>
        <p className="mt-1 text-xs text-brand-muted">{sub}</p>
      </div>
      {["sedan", "suv", "van"].map((v) => (
        <div key={v} className="p-4 text-center sm:p-6">
          <span className="font-serif text-xl font-bold text-brand-charcoal sm:text-2xl">${prices[v]}</span>
        </div>
      ))}
    </div>
  );
}
